import React from "react";
import { connect } from "react-redux";
import { Route, RouteComponentProps, Switch, withRouter } from "react-router";
import { GlobalState } from "store/configure-store";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import SwipeUp from "components/Swipe/SwipeUp";
import PatientSearchContainer from "main/PatientSearch/PatientSearchContainer";
import VirtualizedSwipeableViews from "components/Swipe/VirtualizedSwipeableViews";
import SwipeableRoutes from "react-swipeable-routes";
import { updateShowTutorial } from "App";
import styled from "styled-components";
import Brand from "@cc/react-components/Brand";
import Icon from "@cc/react-components/Icon";
import { Container } from "@cc/react-components/Grid";
import { getColorByDescription } from "components/CareConnectMobile-Theme";

interface StateFromProps
  extends RouteComponentProps,
    React.HTMLAttributes<HTMLElement> {}

interface DispatchFromProps {}

const Tutorial = ({
  history,
}: StateFromProps & DispatchFromProps & RouteComponentProps) => {
  const [t] = useTranslation();

  const onSkip = () => {
    updateShowTutorial();
    history.replace("/");
  };

  let visitedStep3: boolean = false;

  const onSwipeHome = () => {
    updateShowTutorial();
    history.replace("/tutorial/done");
  };

  const slideRenderer = (index: number): any => {
    switch (index) {
      case 0:
        return renderTutorialPage1(t, onSkip);
      case 1:
        if (visitedStep3) {
          return renderTutorialPage4(t, onSkip, onSwipeHome);
        }
        return renderTutorialPage2(t, onSkip);
      case 2:
        return renderTutorialPage3(t, onSkip);
    }
  };

  const onchangeDone = (index: number) => {
    if (index === 2) {
      visitedStep3 = true;
    } else if (index === 1) {
      if (visitedStep3) {
        history.replace("/tutorial/up");
      }
      visitedStep3 = false;
    }
  };

  const renderTutorial = () => {
    return (
      <StyledVirtualSwipeable
        index={0}
        slideCount={3}
        overscanSlideBefore={1}
        overscanSlideAfter={1}
        onChangeDone={onchangeDone}
        slideRenderer={slideRenderer}
        containerStyle={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexGrow: 1,
        }}
        style={{
          width: "100%",
          display: "flex",
          height: "100%",
          overflow: "hidden",
        }}
      />
    );
  };

  const renderTutorialDoneContainer = () => {
    return (
      <SwipeableRoutes
        containerStyle={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexGrow: 1,
        }}
        style={{
          width: "100%",
          display: "flex",
          height: "100%",
          overflow: "hidden",
        }}
        enableMouseEvents
        replace
        ignoreNativeScroll={true}
      >
        <Route
          path="/tutorial/done"
          exact
          render={() => renderTutorialDone()}
        />
        <Route path="/" exact component={PatientSearchContainer} />
      </SwipeableRoutes>
    );
  };

  const renderTutorialDone = () => {
    return (
      <div
        className="h-100 d-flex flex-column justify-content-center align-items-center"
        key="tutorial-page-done"
      >
        <div className="flex-grow-1" />
        <h1>{t("tutorial.done_title")}</h1>
        <h2 className="mb-5">{t("tutorial.done_message")} </h2>
        <Brand application="Visit" mobile />
        <div className="flex-grow-1" />
        <div className="d-flex mb-5">
          <Icon
            circled
            type="chevron-left"
            className="bg-light text-secondary"
            size="lg"
          />
          <Icon
            circled
            type="chevron-left"
            className="bg-light text-secondary ml-2"
            size="lg"
          />
          <Icon
            circled
            type="chevron-left"
            className="bg-light text-secondary ml-2"
            size="lg"
          />
        </div>
      </div>
    );
  };

  return (
    <Switch>
      <Route path="/tutorial" exact render={() => renderTutorial()} />
      <Route
        path="/tutorial/done"
        exact
        render={() => renderTutorialDoneContainer()}
      />
      <Route
        path="/tutorial/up"
        exact
        render={() => renderTutorialPage4(t, onSkip, onSwipeHome)}
      />
    </Switch>
  );
};

const renderTutorialPage1 = (t: TFunction, onSkip: () => void): any => {
  return (
    <div className="h-100 d-flex flex-column" key="tutorial-page-1">
      <Container className="d-flex flex-column mt-3">
        <h3 className="align-self-end" onClick={onSkip}>
          {t("tutorial.skip")}
        </h3>
        <section>
          <h1>{t("tutorial.welcome_title")}</h1>
        </section>
        <section>
          <Brand application="Visit" mobile />
        </section>
        <section>
          <h2>{t("tutorial.welcome_subtitle")}</h2>
        </section>
        <h2>{t("tutorial.welcome_subtitle2")}</h2>
      </Container>
      <div className="h-100 d-flex flex-fill justify-content-end align-items-center">
        <h2 className="my-2">{t("tutorial.action_1")}</h2>
        <SwipeLeftContainer className="h-100 w-25 d-flex pl-3 ml-3 pr-5 justify-content-start align-items-center bg-primary">
          <Icon
            className="text-dark bg-light"
            size="lg"
            circled
            type="chevron-left"
          />
        </SwipeLeftContainer>
      </div>
    </div>
  );
};

const renderTutorialPage2 = (t: TFunction, onSkip: () => void): any => {
  return (
    <div className="h-100 d-flex flex-column" key="tutorial-page-2">
      <Container className="d-flex flex-column mt-3">
        <h3 className="align-self-end" onClick={onSkip}>
          {t("tutorial.skip")}
        </h3>
        <section>
          <h1>{t("tutorial.action_title")}</h1>
          <h2>{t("tutorial.action_subtitle")}</h2>
        </section>
      </Container>
      <div className="h-100 d-flex flex-fill justify-content-end align-items-center">
        <h2 className="text-right">{t("tutorial.action_2")}</h2>
        <SwipeLeftContainer className="h-100 w-25 d-flex pl-3 ml-3 pr-5 justify-content-start align-items-center bg-primary">
          <Icon
            circled
            type="chevron-left"
            className="text-dark bg-light"
            size="lg"
          />
        </SwipeLeftContainer>
      </div>
    </div>
  );
};

const renderTutorialPage3 = (t: TFunction, onSkip: () => void): any => {
  return (
    <div className="h-100 d-flex flex-column" key="tutorial-page-3">
      <Container className="d-flex flex-column mt-3">
        <h3 className="align-self-end" onClick={onSkip}>
          {t("tutorial.skip")}
        </h3>
        <section>
          <h1>{t("tutorial.action_title")}</h1>
          <h2>{t("tutorial.action_subtitle")}</h2>
        </section>
      </Container>
      <div className="h-100 d-flex flex-fill justify-content-start align-items-center">
        <SwipeRightContainer className="h-100 w-25 d-flex pr-3 mr-3 pl-5 justify-content-end align-items-center bg-primary">
          <Icon
            circled
            type="chevron-right"
            className="text-dark bg-light"
            size="lg"
          />
        </SwipeRightContainer>
        <h2 className="text-left">{t("tutorial.action_3")}</h2>
      </div>
    </div>
  );
};

const renderTutorialPage4 = (
  t: TFunction,
  onSkip: () => void,
  onSwipeHome: any
): any => {
  return (
    <SwipeUp
      enableSwipe={true}
      onSwipeEnd={onSwipeHome}
      swipeMessageKey="tutorial.action_4_swipe"
      swipeConfirmKey="tutorial.action_4_result"
      key="tutorial-page-4"
    >
      <div className="h-100 d-flex flex-column">
        <Container className="d-flex flex-column mt-3">
          <h3 className="align-self-end" onClick={onSkip}>
            {t("tutorial.skip")}
          </h3>
          <section>
            <h1>{t("tutorial.action_title")}</h1>
            <h2>{t("tutorial.action_subtitle")}</h2>
          </section>
        </Container>
        <div className="flex-grow-1" />
        <div className="d-flex flex-column justify-content-end align-items-center">
          <h2 className="text-center">{t("tutorial.action_4")}</h2>
          <SwipeUpContainer className="w-100 d-flex py-3 mt-3 pb-5 justify-content-center align-items-center bg-primary">
            <Icon
              circled
              type="chevron-up"
              className="text-dark bg-light"
              size="lg"
            />
          </SwipeUpContainer>
        </div>
      </div>
    </SwipeUp>
  );
};

const SwipeLeftContainer = styled.div`
  clip-path: polygon(0% 50%, 100% 0%, 100% 100%);
`;

const SwipeRightContainer = styled.div`
  clip-path: polygon(0% 0%, 0% 100%, 100% 50%);
`;

const SwipeUpContainer = styled.div`
  clip-path: polygon(0% 100%, 50% 0%, 100% 100%);
`;

const StyledVirtualSwipeable = styled(VirtualizedSwipeableViews)`
  .react-swipeable-view-container {
    div[aria-hidden="true"] {
      background: ${(props) => getColorByDescription(props, "primary")};
      * {
        color: ${(props) => getColorByDescription(props, "primary")} !important;
        background-color: ${(props) =>
          getColorByDescription(props, "primary")} !important;
      }
    }

    div[aria-hidden="false"] {
      background: transparent;
      -webkit-transition: all 1s ease-in-out;
      -moz-transition: all 1s ease-in-out;
      -o-transition: all 1s ease-in-out;
      transition: all 1s ease-in-out;
    }
  }
`;

export default connect<
  StateFromProps,
  DispatchFromProps,
  RouteComponentProps,
  GlobalState
>(
  null,
  {}
)(withRouter(Tutorial));
